// Shared enum → human-readable label layer. DB enum values (e.g. "active", "joint_controller",
// "in_progress") were being rendered raw across tables and detail screens; this localizes the common ones
// (i18n keys `enum.<domain>.<value>`, NL/EN) and humanizes any unmapped value ("dpia_section" → "DPIA section")
// so no raw snake_case enum ever surfaces. UI-polish (cross-app consistency pass).

// Domain acronyms kept uppercase (with a couple of mixed-case exceptions) when humanising enum tokens.
const ACRONYMS: Record<string, string> = {
  dpia: "DPIA", avg: "AVG", gdpr: "GDPR", fg: "FG", dpo: "DPO", scc: "SCC", bcr: "BCR", crm: "CRM",
  dsar: "DSAR", ai: "AI", eer: "EER", eu: "EU", us: "US", iso: "ISO", nen: "NEN", kpi: "KPI",
  wp: "WP", pbd: "PbD", ropa: "RoPA",
};

/** Fallback for any unmapped enum value: snake/lower → sentence case with domain acronyms preserved
 *  ("dpia_section" → "DPIA section", "in_progress" → "In progress"). */
export function humanizeEnum(value: unknown): string {
  const s = String(value ?? "").trim();
  if (!s) return "—";
  return s.split("_").map((w, i) => {
    const lw = w.toLowerCase();
    if (ACRONYMS[lw]) return ACRONYMS[lw];
    return i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : lw;
  }).join(" ");
}

type T = (key: string, ...args: (string | number)[]) => string;

/** Localized label for an enum value: tries i18n `enum.<domain>.<value>`, else humanizes the raw value. */
export function enumLabel(t: T, domain: string, value: unknown): string {
  const v = String(value ?? "").trim();
  if (!v) return "—";
  const key = `enum.${domain}.${v}`;
  const r = t(key);
  return r === key ? humanizeEnum(v) : r;
}
